import type { ReaderEntry } from "./links";

/** A reader entry and the declining landing of the page it leaves. */
export interface ReaderStart {
  entry: ReaderEntry;
  landing: string;
}

// A page restored from the back-forward cache keeps the state it left in:
// a skeleton mid-start, or a signed-out view the round trip has since
// signed in. A reload reads the session afresh, and its URL declines.
export function reloadWhenRestored(win: Window = window): void {
  win.addEventListener("pageshow", (event) => {
    if (event.persisted) win.location.reload();
  });
}

export function entryForm(
  entry: Extract<ReaderEntry, { method: "post" }>,
  doc: Document = document,
): HTMLFormElement {
  const form = doc.createElement("form");
  form.method = "post";
  form.action = entry.action;
  form.hidden = true;
  for (const [name, value] of Object.entries(entry.fields)) {
    const input = doc.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.append(input);
  }
  return form;
}

/**
 * Leaves for the Worker's reader entry without leaving a page behind that
 * starts again. A GET replaces this page. A POST cannot, so this page's
 * own entry first becomes the attempt's landing: Back returns to a URL that
 * declines to start, and the landing that replaced the POST is a GET, so
 * neither entry offers to resubmit the form.
 */
export function enterReaderSession(
  { entry, landing }: ReaderStart,
  win: Window = window,
  submit: (form: HTMLFormElement) => void = (form) => form.submit(),
): void {
  if (entry.method === "get") {
    win.location.replace(entry.href);
    return;
  }
  win.history.replaceState(win.history.state, "", landing);
  reloadWhenRestored(win);
  const form = entryForm(entry, win.document);
  win.document.body.append(form);
  submit(form);
}
